<script lang="ts">
  import { untrack } from "svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import CreateWorkflowActionBox from "../../../../../../../../_shared/_modules/create-workflow-action-box"
  import CreateWorkflowStepFrame from "../../../../../../../../_shared/_modules/create-workflow-step-frame"
  import StepActionCTA from "../../../../../../../../../../_shared/_modules/step-action-cta"
  import { buildEmployeeOptions } from "../../../../../../../../../../../../_shared/_logic/form-state"
  import type {
    BlockerCreateInit,
    BlockerCreateWorkflowEvent,
    BlockerEventType,
    BlockerGeneralOutput,
  } from "../../_shared/_logic/create-blocker-contract"

  type Props = {
    init: BlockerCreateInit
    output?: BlockerGeneralOutput
    readOnly?: boolean
    onEvent: (event: BlockerCreateWorkflowEvent) => void | Promise<void>
    onError?: (message: string | null) => void
  }

  const {
    init,
    output = undefined,
    readOnly = false,
    onEvent,
    onError = () => {},
  }: Props = $props()

  const eventTypeOptions = [
    { name: "Abwesenheit", value: "ABSENCE" as BlockerEventType },
    { name: "Interner Blocker", value: "INTERNAL_BLOCK" as BlockerEventType },
  ]
  const validEventTypes = new Set<BlockerEventType>(["ABSENCE", "INTERNAL_BLOCK"])
  const employeeOptions = $derived(buildEmployeeOptions(init.employees))

  const initialGeneral = untrack(() => ({
    eventType: output?.eventType ?? "INTERNAL_BLOCK",
    employeeId: output?.employeeId ?? init.defaultEmployeeId ?? "",
  }))

  let eventType = $state<BlockerEventType | "">(initialGeneral.eventType)
  let employeeId = $state(initialGeneral.employeeId)
  const validEmployeeIds = $derived(
    new Set(employeeOptions.map((option) => String(option.value)).filter(Boolean)),
  )
  const validationMessage = $derived.by(() => {
    if (!validEventTypes.has(eventType as BlockerEventType)) return "Bitte eine Art auswählen."

    if (!employeeId || !validEmployeeIds.has(employeeId))
      return "Bitte einen Mitarbeiter auswählen."

    return null
  })
  const canComplete = $derived(!validationMessage)

  const complete = () => {
    onError(null)

    if (validationMessage) {
      onError(validationMessage)

      return
    }

    void onEvent({
      type: "general.completed",
      output: {
        eventType: eventType as BlockerEventType,
        employeeId,
      },
    })
  }
</script>

<CreateWorkflowStepFrame
  title="Allgemein"
  description="Wählen Sie, was im Kalender blockiert werden soll und für wen der Blocker gilt."
  helpModalTextId="blockerCreateGeneral"
>
  <CreateWorkflowActionBox label="Blocker-Art" actions={!readOnly ? actionSnippet : null}>
    <p
      >Blocker sind interne Kalendereinträge. Sie erzeugen keinen Auftrag und keine
      Kundeninformation.</p
    >
    <div class="create-workflow-field-grid">
      <Input
        type="select"
        options={eventTypeOptions}
        placeholder="Art"
        bind:value={eventType}
        surfaceTone="dark"
        disabled={readOnly}
      />
      <Input
        type="select"
        options={employeeOptions}
        placeholder="Mitarbeiter"
        bind:value={employeeId}
        surfaceTone="dark"
        disabled={readOnly}
      />
    </div>
  </CreateWorkflowActionBox>
</CreateWorkflowStepFrame>

{#snippet actionSnippet()}
  <StepActionCTA
    type="primaryButton"
    content="Weiter zum Zeitraum"
    disabled={!canComplete}
    disabledReason={validationMessage}
    onClick={complete}
  />
{/snippet}
